import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Modal } from 'framework/ui/ui-builder';
import { CreateForm } from 'framework/form';
import { IssueItem, IssuesModalProps, IssueStatus } from './shared/types';
import { useModalHandler } from 'framework/ui/dashboard';
import IssuesFormContent from './createUpdate/IssuesFormContent';

const DEFAULT_POSTPONE_OPTIONS = {
  ONE_WEEK: { label: '1 Week', value: { postponeWeeks: 1 } },
  TWO_WEEKS: { label: '2 Weeks', value: { postponeWeeks: 2 } },
  ONE_MONTH: { label: '1 Month', value: { postponeMonths: 1 } },
} as const;

const IssuesModal: React.FC<IssuesModalProps> = ({
  isOpen,
  data = [],
  onClose,
  globalData,
  submitFormFunc,
  afterSubmit,
  callbacks,
  config = {},
}) => {
  const [issues, setIssues] = useState<Partial<IssueItem>[]>([]);
  const [postponeKey, setPostponeKey] = useState<string>();
  const [postponeReason, setPostponeReason] = useState<string>();

  const initialFormData = useMemo(() => {
    return {
      issues: [],
      postponeKey: undefined,
      postponeReason: undefined,
    };
  }, []);

  const { handleFormSubmit, handleAfterSubmit } = useModalHandler({
    submitFormFunc: async formData => {
      const hasPostponedIssues = issues.some(issue => issue.status === IssueStatus.POSTPONED);
      const postponedIssues = issues.filter(issue => issue.status === IssueStatus.POSTPONED);
      const nonPostponedIssues = issues.filter(issue => issue.status !== IssueStatus.POSTPONED);

      if (hasPostponedIssues) {
        const regularRequestData = {
          issues: nonPostponedIssues.map(issue => {
            if (issue.isNew) {
              return { description: issue.description };
            }
            return {
              ...issue,
              status: issue.status,
              isNew: issue.isNew || false,
            };
          }),
          isClosedIssuesPostponed: false,
          postponeInformation: null,
          targetZone: globalData?.targetZone as string | undefined,
        };

        const postponedRequestData = {
          issues: postponedIssues.map(issue => {
            if (issue.isNew) {
              return { description: issue.description };
            }
            return {
              ...issue,
              status: IssueStatus.CLOSED,
              isNew: issue.isNew || false,
            };
          }),
          isClosedIssuesPostponed: true,
          postponeInformation:
            postponeKey && postponeReason
              ? {
                  postponeReason,
                  issuesPostponePeriod: DEFAULT_POSTPONE_OPTIONS[postponeKey as keyof typeof DEFAULT_POSTPONE_OPTIONS]
                    ?.value || { postponeWeeks: 1 },
                }
              : null,
          targetZone: globalData?.targetZone as string | undefined,
        };

        if (submitFormFunc) {
          if (nonPostponedIssues.length > 0) {
            await submitFormFunc(regularRequestData);
          }
          return await submitFormFunc(postponedRequestData);
        }
        if (callbacks?.onSubmit) {
          if (nonPostponedIssues.length > 0) {
            await callbacks.onSubmit(nonPostponedIssues, regularRequestData);
          }
          return await callbacks.onSubmit(postponedIssues, postponedRequestData);
        }
        return postponedRequestData;
      } else {
        const requestData = {
          issues: issues.map(issue => {
            if (issue.isNew) {
              return { description: issue.description };
            }
            return {
              ...issue,
              status: issue.status,
              isNew: issue.isNew || false,
            };
          }),
          isClosedIssuesPostponed: false,
          postponeInformation: null,
          targetZone: globalData?.targetZone as string | undefined,
        };

        if (submitFormFunc) {
          return await submitFormFunc(requestData);
        }
        if (callbacks?.onSubmit) {
          return await callbacks.onSubmit(issues, requestData);
        }
        return requestData;
      }
    },
    afterSubmit,
    onClose,
  });

  const createIssue = useCallback(
    (issueData: Partial<IssueItem>, isNew = false): Partial<IssueItem> => ({
      ...issueData,
      status: issueData.status || IssueStatus.ACTIVE,
      id: issueData.id || crypto.randomUUID(),
      createdAt: issueData.createdAt || new Date().toISOString(),
      updatedAt: issueData.updatedAt || null,
      closedDate: issueData.closedDate || null,
      retentionRiskId: issueData.retentionRiskId || null,
      isNew,
    }),
    [],
  );

  const getIssuesFromData = useCallback((): Partial<IssueItem>[] => {
    if (Array.isArray(data) && data.length > 0) {
      return data.map(issue => createIssue(issue, false));
    }

    const employeeIssues = (data as any)?.employeeData?.retentionRiskIssues;
    if (Array.isArray(employeeIssues) && employeeIssues.length > 0) {
      return employeeIssues.map((issue: IssueItem) => createIssue(issue, false));
    }

    const predefinedIssues = globalData?.predefinedIssues as IssueItem[];
    if (Array.isArray(predefinedIssues) && predefinedIssues.length > 0) {
      return predefinedIssues.map(issue => createIssue(issue, false));
    }

    return [createIssue({ description: '' }, true)];
  }, [data, globalData, createIssue]);

  useEffect(() => {
    if (isOpen) {
      setIssues(getIssuesFromData());
    }
  }, [isOpen, getIssuesFromData]);

  return (
    <CreateForm submitFormFunc={handleFormSubmit} initialData={initialFormData} afterSubmit={handleAfterSubmit}>
      <Modal isOpen={isOpen} onToggle={onClose} modalSize="sm" hasCancelButton={false}>
        <IssuesFormContent
          issues={issues}
          setIssues={setIssues}
          postponeKey={postponeKey}
          postponeReason={postponeReason}
          setPostponeKey={setPostponeKey}
          setPostponeReason={setPostponeReason}
          config={config}
          createIssue={createIssue}
          onClose={onClose}
          globalData={globalData}
        />
      </Modal>
    </CreateForm>
  );
};

export default IssuesModal;
